import { devDebug, devWarn } from "@/util/dev.mjs";

// A limited deep equality check that mainly checks JSON-serializable data.
//
// For performance reasons, it will self-terminate and return FALSE after
// a certain number of objects compared, or if a cycle is detected.
export default function deepEqual(
  a: unknown,
  b: unknown,
  limit = 1000,
  state = { count: 0, seen: new WeakSet() },
) {
  if (a === b) {
    return true;
  }

  if (a && b && typeof a === "object" && typeof b === "object") {
    state.count++;

    if (state.count > limit) {
      devDebug(`Deep equality check exceeded threshold of ${limit} objects.`, {
        a,
        b,
      });

      return false;
    }

    if (state.seen.has(a) || state.seen.has(b)) {
      devWarn(`Deep equality cycle detected!`, {
        obj: state.seen.has(a) ? a : b,
      });
      return false;
    }

    state.seen.add(a);
    state.seen.add(b);

    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;

      for (const key in a) {
        const result = deepEqual(a[key], b[key], limit, state);
        if (!result) return false;
      }

      return true;
    }

    if (a.constructor !== Object || b.constructor !== Object) {
      // If they're not the same type of object, then we know they're not equal
      if (a.constructor !== b.constructor) {
        return false;
      }

      // We likely cannot compare objects that are not... *Object* objects. Add special
      // handling for certain ones.

      if (a instanceof Promise || b instanceof Promise) {
        return false;
      }

      if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
      }
    }

    if (Object.keys(a).length !== Object.keys(b).length) return false;

    const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);

    for (const key of allKeys.values()) {
      const result = deepEqual(
        a[key as keyof typeof a],
        b[key as keyof typeof b],
        limit,
        state,
      );
      if (!result) return false;
    }

    return true;
  }

  return false;
}

export function shallowEqual(a: unknown, b: unknown) {
  if (a && b && typeof a === "object" && typeof b === "object") {
    for (const key in a) {
      if (!(key in b)) return false;
      if (a[key as keyof typeof a] !== b[key as keyof typeof b]) return false;
    }
    return Object.keys(a).length === Object.keys(b).length;
  }
  return false;
}
